import { observer } from 'mobx-react-lite';
import { useStore } from '@shared/hooks/useStore';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, SCREEN_TYPE } from '@/shared/configs/constants';
import { Button } from '@shared/components/common/Button';
import { Input } from '@shared/components/common/Input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import { useEffect, useState } from 'react';
import { toastify } from '@shared/utils/toastify';
import { handleBeforeInputNumber, handleInputNumberWithMaxLength, handlePasteNumber, KataNumberReg } from '@shared/utils/common';
import { REGEX_VALIDATE_EMAIL } from '@shared/components/constants/common.constants';

const schema = yup.object().shape({
    lastName: yup.string().required('error.required'),
    firstName: yup.string().required('error.required'),
    firstNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
    lastNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
    email: yup
        .string()
        .required('error.required')
        .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email'),
    confirmEmail: yup
        .string()
        .required('error.required')
        .matches(REGEX_VALIDATE_EMAIL, 'form_error_validate.email')
        .oneOf([yup.ref('email')], 'error.email_confirm_not_match'),
    companyName: yup.string().trim().required('error.required'),
    companyNameFuri: yup.string().trim().required('error.required').matches(KataNumberReg, 'error.format_hira_kata'),
    phoneNumber: yup.string().required('error.required').typeError('error.number_positive')
});

type FormData = yup.InferType<typeof schema>;

const AdminProfileEditPage = observer(() => {
    const { profileStore: { profile, getUserProfile, updateGroupAdminProfile } } = useStore();
    const navigate = useNavigate();

    const form = useForm<FormData>({
        resolver: yupResolver(schema),
        mode: 'onChange'
    });

    const [isOnlyView, setIsOnlyView] = useState<boolean>(false);

    useEffect(() => {
        const loadUserData = async () => {
            await getUserProfile();
        };
        loadUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (profile) {
            form.setValue('lastName', profile.lastName || '');
            form.setValue('firstName', profile.firstName || '');
            form.setValue('lastNameFuri', profile.lastNameFuri || '');
            form.setValue('firstNameFuri', profile.firstNameFuri || '');
            form.setValue('email', profile.email || '');
            form.setValue('confirmEmail', profile.email || '');
            form.setValue('companyName', profile.companyName || '');
            form.setValue('companyNameFuri', profile.companyNameFuri || '');
            form.setValue('phoneNumber', profile.phoneNumber || '');
            setIsOnlyView(!!profile.isSynchronizedRecord);
        }
    }, [profile, form]);

    const onSubmit = async (data: FormData) => {
        const payload = {
            ...data,
            customerCode: profile?.userId || '',
            screenType: SCREEN_TYPE.GA
        };
        const success = await updateGroupAdminProfile(payload);
        if (success) {
            toastify('success', 'プロフィールを更新しました');
            navigate(ADMIN_ROUTES.profile.href);
        }
    };

    if (!profile) return null;

    return (
        <div className='space-y-8'>
            <section className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-10'>
                <h2 className='text-[20px] leading-[36px] font-semibold mb-8'>個人情報</h2>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                        {/* Name */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            <FormField
                                control={form.control}
                                name='lastName'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>姓</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.lastName ? 'border-red-500' : ''}`} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='firstName'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>名</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.firstName ? 'border-red-500' : ''}`} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Name Furigana */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            <FormField
                                control={form.control}
                                name='lastNameFuri'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>姓（フリガナ）</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.lastNameFuri ? 'border-red-500' : ''}`} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='firstNameFuri'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>名（フリガナ）</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.firstNameFuri ? 'border-red-500' : ''}`} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Email */}
                        <FormField
                            control={form.control}
                            name='email'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>メールアドレス</FormLabel>
                                    <FormControl>
                                        <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.email ? 'border-red-500' : ''}`} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Confirm Email */}
                        <FormField
                            control={form.control}
                            name='confirmEmail'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>メールアドレスの確認</FormLabel>
                                    <FormControl>
                                        <Input {...field} disabled={isOnlyView} className={`h-14 rounded-none ${form.formState.errors.confirmEmail ? 'border-red-500' : ''}`} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='h-[1px] bg-[#D9D9D9] my-6' />

                        {/* Contact Info Section Header */}
                        <h2 className='text-[20px] leading-[36px] font-semibold mb-6'>連絡先情報</h2>

                        {/* Company Name */}
                        <FormField
                            control={form.control}
                            name='companyName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>所属会社名</FormLabel>
                                    <FormControl>
                                        <Input {...field} disabled className='h-14 rounded-none bg-[#CCCCCC] text-[#666666]' />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        {/* Company Name Furigana */}
                        <FormField
                            control={form.control}
                            name='companyNameFuri'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>会社名（フリガナ）</FormLabel>
                                    <FormControl>
                                        <Input {...field} disabled className='h-14 rounded-none bg-[#CCCCCC] text-[#666666]' />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        {/* Phone Number */}
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>電話番号</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={isOnlyView}
                                            className={`h-14 rounded-none ${form.formState.errors.phoneNumber ? 'border-red-500' : ''}`}
                                            onInput={e => handleInputNumberWithMaxLength(e, 20)}
                                            maxLength={20}
                                            onBeforeInput={handleBeforeInputNumber}
                                            onPaste={handlePasteNumber}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Actions */}
                        <div className='flex gap-4 pt-4'>
                            <Button
                                type='button'
                                variant='outline'
                                className='flex-1 h-[56px] bg-[#676767] text-white'
                                onClick={() => navigate(ADMIN_ROUTES.profile.href)}
                            >
                                {isOnlyView ? '戻る' : 'キャンセル'}
                            </Button>
                            {!isOnlyView && (
                                <Button
                                    type='submit'
                                    className='flex-1 h-[56px] bg-primary text-white'
                                >
                                    登録
                                </Button>
                            )}
                        </div>
                    </form>
                </Form>
            </section>
        </div>
    );
});

export default AdminProfileEditPage;
